import type {
  CharacteristicFilterType,
  StoreShortWithSupplierType,
} from '@/types'

import type { ParsedUrlFilters, SearchParams } from './filtersTypes'

type ParsedCharacteristic = CharacteristicFilterType

type ParsedCharacteristicFilters = {
  filters?: Record<string, string[]>
  ranges?: Record<string, { min?: string; max?: string }>
}

export class FiltersUrlService {
  parseSearchParams(searchParams: URLSearchParams): SearchParams {
    const leftoversVal =
      searchParams.get('withLeftovers') || searchParams.get('leftovers')
    const discontinuedVal = searchParams.get('discontinued')

    let withLeftovers: boolean | undefined = undefined
    if (leftoversVal === 'true') withLeftovers = true
    else if (leftoversVal === 'false') withLeftovers = false

    let discontinued: boolean | undefined = undefined
    if (discontinuedVal === 'true') discontinued = true
    else if (discontinuedVal === 'false') discontinued = false

    const matchUniformSlottedCharacteristics =
      searchParams.get('matchUniformSlottedCharacteristics') === 'true'

    const characteristicMap: Record<string, string[]> = {}
    const rangeMap: Record<string, { min?: string; max?: string }> = {}
    const storesMap: Record<`stores.include[${string}]`, string> = {}

    searchParams.forEach((value, key) => {
      if (key.startsWith('stores.include[')) {
        storesMap[key as `stores.include[${string}]`] = value
      }
      const filterMatch = key.match(/^filter\[(\d+)\]$/)
      if (filterMatch) {
        const code = filterMatch[1]
        if (!characteristicMap[code]) characteristicMap[code] = []
        characteristicMap[code].push(value)
      }
      const rangeMatch = key.match(/^ranges\[(\d+)\]\.(min|max)$/)
      if (rangeMatch) {
        const code = rangeMatch[1]
        const type = rangeMatch[2] as 'min' | 'max'
        if (!rangeMap[code]) rangeMap[code] = {}
        rangeMap[code][type] = value
      }
    })

    return {
      query: searchParams.get('query') || undefined,
      categoryIds: searchParams.get('categoryIds') || undefined,
      brandIds: searchParams.get('brandIds') || undefined,
      parentIds: searchParams.get('parentIds') || undefined,
      productCodes: searchParams.get('productCodes') || undefined,
      withLeftovers,
      discontinued,
      matchUniformSlottedCharacteristics,
      ...storesMap,
      characteristicFilters:
        Object.keys(characteristicMap).length || Object.keys(rangeMap).length
          ? JSON.stringify({ filters: characteristicMap, ranges: rangeMap })
          : undefined,
      page: parseInt(searchParams.get('page') || '0', 10),
    }
  }

  hasSearchParams(params: SearchParams): boolean {
    const dynamicKeys = Object.keys(params).some((k) =>
      k.startsWith('stores.include[')
    )
    return !!(
      params.query ||
      params.categoryIds ||
      params.brandIds ||
      params.parentIds ||
      params.withLeftovers !== undefined ||
      params.discontinued !== undefined ||
      (params.page && params.page > 0) ||
      params.characteristicFilters ||
      dynamicKeys
    )
  }

  parseStoresFromUrl(
    searchParams: URLSearchParams
  ): StoreShortWithSupplierType[] {
    const result: StoreShortWithSupplierType[] = []
    searchParams.forEach((value, key) => {
      const match = key.match(/^stores\.include\[(.+)\]$/)
      if (match) {
        const supplierId = match[1]
        value.split(',').forEach((codeStr) => {
          const code = parseInt(codeStr.trim(), 10)
          if (!isNaN(code)) {
            const compositeId = `${supplierId}:${code}`
            result.push({
              id: compositeId,
              code,
              supplierId,
              description: ``,
              owner: '',
              isRemote: false,
              isActive: true,
            })
          }
        })
      }
    })
    return result
  }

  parseCharacteristics(
    characteristicFilters?: string
  ): CharacteristicFilterType[] {
    if (!characteristicFilters) return []
    try {
      const parsed: ParsedCharacteristicFilters = JSON.parse(
        characteristicFilters
      )
      const resultMap = new Map<number, ParsedCharacteristic>()

      const ensureEntry = (code: number, type: 'REFERENCE' | 'NUMERIC') => {
        const current: ParsedCharacteristic = resultMap.get(code) ?? {
          code,
          name: '',
          type,
          options: [],
          unitOfMeasurementDto: null,
        }
        resultMap.set(code, current)

        if (type === 'NUMERIC') {
          current.type = 'NUMERIC'
        }

        return current
      }

      if (parsed.filters) {
        Object.entries(parsed.filters).forEach(([code, values]) => {
          const entry = ensureEntry(Number(code), 'REFERENCE')
          entry.options = values.map((v) => ({ value: v, count: 0 }))
        })
      }
      if (parsed.ranges) {
        Object.entries(parsed.ranges).forEach(([code, range]) => {
          const entry = ensureEntry(Number(code), 'NUMERIC')
          entry.chosenRange = {
            min: range.min ? parseFloat(range.min) : null,
            max: range.max ? parseFloat(range.max) : null,
          }
        })
      }
      return Array.from(resultMap.values())
    } catch {
      return []
    }
  }

  parseAllFiltersSync(
    searchParams: URLSearchParams,
    defaultLeftovers: boolean = true
  ): ParsedUrlFilters {
    const params = this.parseSearchParams(searchParams)

    return {
      query: params.query || '',
      withLeftovers: params.withLeftovers ?? defaultLeftovers,
      productCodes: params.productCodes
        ? params.productCodes
            .split(',')
            .map(Number)
            .filter((n) => !isNaN(n))
        : undefined,
      withDiscontinued:
        params.discontinued !== undefined ? params.discontinued : true,
      categories: params.categoryIds
        ? params.categoryIds
            .split(',')
            .map((id) => ({ id, description: '', count: 0 }))
        : [],
      brands: params.brandIds
        ? params.brandIds
            .split(',')
            .map((id) => ({ id, description: '', count: 0 }))
        : [],
      parents: params.parentIds
        ? params.parentIds
            .split(',')
            .map((id) => ({ id, description: '', count: 0 }))
        : [],
      stores: this.parseStoresFromUrl(searchParams),
      characteristics: this.parseCharacteristics(params.characteristicFilters),
      matchUniformSlottedCharacteristics:
        params.matchUniformSlottedCharacteristics === true,
      page: params.page || 0,
    }
  }
}

export const filtersUrlService = new FiltersUrlService()
